import type { Employee } from 'src/api/EmployeeService';
import type { DaySegment } from '@api/ShiftTypeService';

const sortEmployeesByPriority = (employees: Employee[]): Employee[] =>
    employees.toSorted((a, b) => {
        const aPriority = a.priority ?? Number.MAX_SAFE_INTEGER;
        const bPriority = b.priority ?? Number.MAX_SAFE_INTEGER;

        if (aPriority === bPriority) {
            return (
                new Date(a.registered).valueOf() -
                new Date(b.registered).valueOf()
            );
        }

        return aPriority - bPriority;
    });

export const DAY_SEGMENT_ORDER: Record<DaySegment, number> = {
    D: 0,
    A: 1,
    L: 2,
    N: 3,
};

/** Do immutable sorting of day segments based on the DAY_SEGMENT_ORDER */
export const sortDaySegments = (daySegments: DaySegment[]): DaySegment[] =>
    daySegments.toSorted((a, b) => DAY_SEGMENT_ORDER[a] - DAY_SEGMENT_ORDER[b]);

const sortShiftCodesByDaySegment = (
    codesToBeSorted: string[] | undefined,
    allShiftTypes: Array<{ code: string; daySegment: DaySegment }>,
): string[] | undefined =>
    codesToBeSorted?.sort((a, b) => {
        const aDaySegment = allShiftTypes.find(
            (sh) => sh.code === a,
        )?.daySegment;
        const bDaySegment = allShiftTypes.find(
            (sh) => sh.code === b,
        )?.daySegment;

        if (aDaySegment === bDaySegment) {
            return a.localeCompare(b, 'no', {
                numeric: true,
                sensitivity: 'base',
            });
        }
        // Missing day segments sort as `undefined`, preserving the
        // previous (untyped) comparison behavior
        return String(
            DAY_SEGMENT_ORDER[aDaySegment as DaySegment],
        ).localeCompare(
            String(DAY_SEGMENT_ORDER[bDaySegment as DaySegment]),
            'no',
            { numeric: true, sensitivity: 'base' },
        );
    });

const sortShiftTypesByDaySegment = <
    T extends { code: string; daySegment: DaySegment },
>(
    shiftTypes: T[] | undefined,
): T[] | undefined =>
    shiftTypes?.toSorted((a, b) => {
        if (a.daySegment === b.daySegment) {
            return a.code.localeCompare(b.code, 'no', {
                numeric: true,
                sensitivity: 'base',
            });
        }
        return String(DAY_SEGMENT_ORDER[a.daySegment]).localeCompare(
            String(DAY_SEGMENT_ORDER[b.daySegment]),
        );
    });

export {
    sortEmployeesByPriority,
    sortShiftCodesByDaySegment,
    sortShiftTypesByDaySegment,
};
